/**
 * Section Components for NCR Edit
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { MessageSquare, Clock, Loader2 } from 'lucide-react';
import { NCRComment, NCRActionLog } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

export { DocumentsSection } from './DocumentsSection';

interface CommentsSectionProps {
  comments: NCRComment[];
  newComment: string;
  onNewCommentChange: (value: string) => void;
  onAddComment: () => void;
}

export function CommentsSection({
  comments,
  newComment,
  onNewCommentChange,
  onAddComment,
}: CommentsSectionProps) {
  const { formatDateTime } = useFormatters();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Communications ({comments.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="border-l-4 border-blue-500 pl-4 py-2">
              <div className="flex items-center justify-between mb-1">
                <p className="font-medium">{comment.created_by_name}</p>
                <p className="text-xs text-muted-foreground">
                  {formatDateTime(comment.created_at)}
                </p>
              </div>
              <p className="text-sm">{comment.comment}</p>
            </div>
          ))}

          <div className="space-y-2">
            <Textarea
              value={newComment}
              onChange={(e) => onNewCommentChange(e.target.value)}
              placeholder="Add a comment..."
              rows={3}
            />
            <Button onClick={onAddComment} disabled={!newComment.trim()}>
              Add Comment
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface ActionLogSectionProps {
  actionLogs: NCRActionLog[];
}

export function ActionLogSection({ actionLogs }: ActionLogSectionProps) {
  // CR-6009: present the workflow action log as a table (When /
  // Company / User / Description) instead of the previous stacked
  // card layout — mirrors the Purchasing > PO Lines > Change Request
  // History table so users only learn one audit-trail layout.
  const formatWhen = (iso: string) => {
    const d = new Date(iso);
    return `${d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })} ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Workflow Action Log ({actionLogs.length})
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Complete audit trail of all NCR activities and status changes
        </p>
      </CardHeader>
      <CardContent>
        {actionLogs.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">No actions recorded yet</p>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-medium whitespace-nowrap">When</th>
                  <th className="px-3 py-2 font-medium">Company</th>
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">Description</th>
                </tr>
              </thead>
              <tbody>
                {actionLogs.map((log) => {
                  const isStatusChange =
                    log.action_type === 'status_changed' ||
                    log.action_type === 'detail_submitted' ||
                    log.action_type === 'rca_submitted' ||
                    log.action_type === 'car_submitted' ||
                    log.action_type === 'car_approved' ||
                    log.action_type === 'ncr_closed' ||
                    log.action_type === 'ncr_reopened';
                  // CR-6017: serializer returns the NCR's supplier name
                  // (the contextual company the workflow targets), not the
                  // acting user's primary company. Em-dash when missing.
                  const company = (log as unknown as { performed_by_company?: string | null }).performed_by_company || '—';
                  return (
                    <tr key={log.id} className="border-t align-top">
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                        {formatWhen(log.performed_at)}
                      </td>
                      <td className="px-3 py-2 text-xs">{company}</td>
                      <td className="px-3 py-2 text-xs">{log.performed_by_name || '—'}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              isStatusChange
                                ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/20 dark:text-blue-400'
                                : 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {log.action_type_display}
                          </Badge>
                          {/* QUALITY-NCR-REVIEW #15: words, not stored codes. A
                              Master Edit from blank shows "(blank) → value". */}
                          {(log.old_value || log.new_value) && (
                            <span className="text-xs text-muted-foreground">
                              {log.old_value_display ?? log.old_value} → {log.new_value_display ?? log.new_value}
                            </span>
                          )}
                          <span className="text-sm">{log.action_description_display ?? log.action_description}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** What the confirm does (24 Sep 2026: approving the 8D and closing the NCR
 *  became separate steps; they combine only when the 8D is the last thing open).
 *  E21 (29 Sep 2026): whenever the customer's action is the last thing open,
 *  they choose -- close now or keep it open. `last_action` asks straight after
 *  a credit or replacement confirmation; `approve_and_close` offers "Approve,
 *  keep open" beside the close. */
export type CloseDialogMode = 'approve_only' | 'approve_and_close' | 'close' | 'last_action';

/** E27: whose CAR is being approved -- the supplier's, or (internal /
 *  customer liability) the customer's own team's. */
const carOwner = (customerOwns8D: boolean) =>
  customerOwns8D ? 'The Corrective Action Report' : 'The supplier\u2019s Corrective Action Report';

const closeDialogCopy = (mode: CloseDialogMode, customerOwns8D: boolean) => ({
  approve_only: {
    title: 'Approve 8D',
    body: `${carOwner(customerOwns8D)} will be approved and the 8D marked complete. The NCR stays open until the other required actions are done.`,
    action: 'Approve 8D',
  },
  approve_and_close: {
    title: 'Approve 8D & Close NCR',
    body: `${carOwner(customerOwns8D)} will be approved. Nothing else is outstanding, so this NCR will close. You can reopen it later if needed.`,
    action: 'Approve & close NCR',
    keepOpen: 'Approve, keep open',
  },
  // No promise about telling the supplier: an internal NCR's supplier never hears of it.
  close: {
    title: 'Close NCR',
    body: 'Every required action is complete. This NCR will close. You can reopen it later if needed.',
    action: 'Close NCR',
  },
  last_action: {
    title: 'Close the NCR too?',
    body: 'That was the last thing outstanding on this NCR. Close it now, or keep it open. You can reopen it later if needed.',
    action: 'Close NCR',
    cancel: 'Keep open',
  },
}[mode] as { title: string; body: string; action: string; keepOpen?: string; cancel?: string });

interface CloseNCRDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
  /** E21: approve the 8D without closing ("Approve, keep open"). */
  onKeepOpen?: () => void | Promise<void>;
  mode?: CloseDialogMode;
  /** The customer's team owns this 8D (internal / customer liability). */
  customerOwns8D?: boolean;
}

export function CloseNCRDialog({
  open, onOpenChange, onClose, onKeepOpen, mode = 'approve_and_close', customerOwns8D = false,
}: CloseNCRDialogProps) {
  const copy = closeDialogCopy(mode, customerOwns8D);
  const [submitting, setSubmitting] = useState(false);
  const run = async (fn: () => void | Promise<void>) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await fn();
    } finally {
      setSubmitting(false);
    }
  };
  const handleConfirm = () => run(onClose);
  // CR-6024 r2: switched from AlertDialog → Dialog so the confirm Button
  // fires reliably (Jacq's rework: "clicks Approve but nothing happens").
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.body}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {copy.cancel ?? 'Cancel'}
          </Button>
          {copy.keepOpen && onKeepOpen && (
            <Button type="button" variant="outline" onClick={() => run(onKeepOpen)} disabled={submitting}>
              {copy.keepOpen}
            </Button>
          )}
          <Button type="button" onClick={handleConfirm} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {copy.action}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** CR-6024: rejection-reason modal for "Reject & Rework" flow.
 *
 * r2 fix: switched from AlertDialog → regular Dialog. AlertDialog has special
 *  focus/dispatch semantics around its AlertDialogAction/Cancel children, and
 *  a plain <Button> inside the AlertDialogFooter wasn't firing reliably on
 *  Jacq's session (the "submit does nothing" rework feedback). Dialog has no
 *  such wrapper requirement — custom buttons work as normal. */
interface RejectCARDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReject: (reason: string) => Promise<boolean> | void;
  /** The customer's team owns this 8D (internal / customer liability). */
  customerOwns8D?: boolean;
}

export function RejectCARDialog({ open, onOpenChange, onReject, customerOwns8D = false }: RejectCARDialogProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim() || submitting) return;
    setSubmitting(true);
    try {
      const ok = await onReject(reason);
      // Parent closes the dialog on success — clear local state for the next open.
      if (ok !== false) setReason('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Clear the reason whenever the dialog closes so a fresh open starts blank.
        if (!next) setReason('');
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject CAR &amp; Send for Rework</DialogTitle>
          <DialogDescription>
            Please provide a reason for rejecting this submission. The NCR will be sent back to{' '}
            {customerOwns8D ? 'your team' : 'the supplier'} for rework.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What needs to change in the Corrective Action Report?"
            rows={5}
            disabled={submitting}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleSubmit}
            disabled={!reason.trim() || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Reject &amp; Send Rework
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RejectNCRDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReject: (reason: string) => Promise<boolean> | void;
}

/** Supplier reject-NCR modal: free-text reason sent to the customer, who then
 *  approves (voids the NCR) or declines. Calls the backend /reject_ncr/ endpoint. */
export function RejectNCRDialog({ open, onOpenChange, onReject }: RejectNCRDialogProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim() || submitting) return;
    setSubmitting(true);
    try {
      const ok = await onReject(reason);
      if (ok !== false) setReason('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setReason('');
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject NCR</DialogTitle>
          <DialogDescription>
            Provide a reason for rejecting this NCR. It is sent to the customer for review — if they
            accept it, the NCR is voided and the reason is recorded in the audit history.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why should this NCR be rejected?"
            rows={5}
            disabled={submitting}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleSubmit} disabled={!reason.trim() || submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Submit Rejection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** CR-6024 r3: customer reopen-closed-NCR confirmation modal. Spec acceptance
 *  criterion "Closed NCRs can be reopened by customer users if required."
 *  Calls the existing backend /reopen_ncr/ endpoint which moves the NCR
 *  from `closed` back to `ca_submitted`. */
interface ReopenNCRDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReopen: () => void;
}

export function ReopenNCRDialog({ open, onOpenChange, onReopen }: ReopenNCRDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onReopen();
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reopen NCR</DialogTitle>
          <DialogDescription>
            This will reopen the closed NCR and return it to the Corrective Action stage so
            additional work can be carried out.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Reopen NCR
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
